import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

// Normalise a backend error into a human-readable string. FastAPI's
// Pydantic 422 responses return `detail` as an ARRAY of objects with
// {type, loc, msg, input, url}; passing that array to <p>{msg}</p> or
// toast.error(msg) crashes React with "Objects are not valid as a React
// child". This helper flattens every shape we've seen from the backend
// into a plain string.
export const stringifyDetail = (detail) => {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        // Pydantic v2 validation errors — one line per field.
        return detail
            .map((it) => {
                if (typeof it === "string") return it;
                const loc = Array.isArray(it?.loc)
                    ? it.loc.filter((p) => p !== "body").join(".")
                    : "";
                const msg = it?.msg || it?.message || JSON.stringify(it);
                return loc ? `${loc}: ${msg}` : msg;
            })
            .join("; ");
    }
    if (typeof detail === "object") {
        return (
            detail.msg ||
            detail.message ||
            detail.detail ||
            detail.error ||
            JSON.stringify(detail)
        );
    }
    return String(detail);
};

// One-liner used across catch blocks — pulls .detail or .message from an
// axios error and coerces to a string.
export const errText = (e, fallback = "Request failed") =>
    stringifyDetail(e?.response?.data?.detail) ||
    stringifyDetail(e?.response?.data) ||
    e?.message ||
    fallback;

export const runSearch = (payload) => api.post("/search", payload).then((r) => r.data);
export const listHistory = () => api.get("/history").then((r) => r.data);
export const getHistoryItem = (id) => api.get(`/history/${id}`).then((r) => r.data);
export const deleteHistoryItem = (id) => api.delete(`/history/${id}`).then((r) => r.data);
export const generateLandingPage = (recordId) =>
    api
        .post("/landing-page", { record_id: recordId }, { timeout: 120000 })
        .then((r) => r.data);
export const generateDataDrivenLanding = (recordId) =>
    api
        .post("/data-driven-landing", { record_id: recordId }, { timeout: 120000 })
        .then((r) => r.data);
export const scrapeUrl = (url, business_type = "", city = "") =>
    api
        .post(
            "/scrape-url",
            { url, business_type, city },
            { timeout: 60000 }
        )
        .then((r) => r.data);

export const enrichKeywords = ({ keywords, country, city = "", classify_intent = true }) =>
    api
        .post(
            "/keywords/enrich",
            { keywords, country, city, classify_intent },
            { timeout: 120000 }
        )
        .then((r) => r.data);

// ---------- Google Ads ----------
export const adsStatus = () => api.get("/ads/status").then((r) => r.data);
export const adsOauthStart = () => api.get("/ads/oauth/start").then((r) => r.data);
export const adsDisconnect = () => api.post("/ads/oauth/disconnect").then((r) => r.data);
export const adsListAccounts = () => api.get("/ads/accounts").then((r) => r.data);
export const adsListCampaigns = (customer_id) =>
    api.get(`/ads/campaigns?customer_id=${customer_id}`).then((r) => r.data);
export const adsSearchTerms = (customer_id, date_range = "LAST_30_DAYS", account_name = "", campaign_ids = []) =>
    api
        .post(
            "/ads/reports/search-terms",
            { customer_id, date_range, account_name, campaign_ids },
            { timeout: 120000 }
        )
        .then((r) => r.data);
export const adsGeographic = (customer_id, date_range = "LAST_30_DAYS", account_name = "") =>
    api
        .post("/ads/reports/geographic", { customer_id, date_range, account_name }, { timeout: 120000 })
        .then((r) => r.data);
export const adsAuditsList = () => api.get("/ads/audits").then((r) => r.data);
export const adsAuditGet = (id) => api.get(`/ads/audits/${id}`).then((r) => r.data);
export const adsAuditRename = (id, label) => api.patch(`/ads/audits/${id}`, { label }).then((r) => r.data);
export const adsAuditDelete = (id) => api.delete(`/ads/audits/${id}`).then((r) => r.data);
export const adsDemographics = (customer_id, date_range = "LAST_30_DAYS", account_name = "") =>
    api
        .post("/ads/reports/demographics", { customer_id, date_range, account_name }, { timeout: 120000 })
        .then((r) => r.data);
export const adsDemoRecommend = (age, gender, income, meta = {}) =>
    api
        .post(
            "/ads/demographics/recommend",
            {
                age,
                gender,
                income,
                account_name: meta.account_name || "",
                date_range: meta.date_range || "",
            },
            { timeout: 120000 }
        )
        .then((r) => r.data);

export const adsDemoApply = (payload) =>
    api
        .post("/ads/demographics/apply", payload, { timeout: 90000 })
        .then((r) => r.data);

export const adsDemoApplied = (customer_id) =>
    api
        .get("/ads/demographics/applied", { params: { customer_id }, timeout: 30000 })
        .then((r) => r.data);

export const adsDemoUnapply = (payload) =>
    api
        .post("/ads/demographics/unapply", payload, { timeout: 30000 })
        .then((r) => r.data);

export const adsDemoUnapplyAll = (customer_id, only_failed = false) =>
    api
        .post(
            "/ads/demographics/unapply-all",
            { customer_id, only_failed },
            { timeout: 30000 }
        )
        .then((r) => r.data);

export const adsDemoImpact = (customer_id) =>
    api
        .post("/ads/demographics/impact", { customer_id }, { timeout: 120000 })
        .then((r) => r.data);

export const adsDemoImpactStatus = (id, status) =>
    api
        .post("/ads/demographics/impact/status", { id, status }, { timeout: 30000 })
        .then((r) => r.data);

export const adsDemoImpactRevert = (id) =>
    api
        .post("/ads/demographics/impact/revert", { id }, { timeout: 90000 })
        .then((r) => r.data);

export const adsSurgicalTargeting = (customer_id, date_range = "LAST_30_DAYS", account_name = "", campaign_ids = []) =>
    api
        .post(
            "/ads/surgical-targeting/report",
            { customer_id, date_range, account_name, campaign_ids: Array.isArray(campaign_ids) ? campaign_ids : [] },
            { timeout: 180000 },
        )
        .then((r) => r.data);

export const adsSurgicalAnalyze = (payload) =>
    api
        .post("/ads/surgical-targeting/analyze", payload, { timeout: 120000 })
        .then((r) => r.data);

// ---- Keyword Planner (native Google Ads) ----
export const keywordPlannerOptions = () =>
    api.get("/ads/keyword-planner/options").then((r) => r.data);
export const keywordPlannerIdeas = (payload) =>
    api
        .post("/ads/keyword-planner/ideas", payload, { timeout: 120000 })
        .then((r) => r.data);
export const keywordPlannerIdeasDFS = (payload) =>
    api
        .post("/ads/keyword-planner/ideas-dfs", payload, { timeout: 120000 })
        .then((r) => r.data);
export const keywordPlannerGeoSuggest = (query, country, language = "en") =>
    api
        .post(
            "/ads/keyword-planner/geo-suggest",
            { query, country, language },
            { timeout: 30000 }
        )
        .then((r) => r.data);

// ---- Campaign Builder (Enrich → Plan → Push) ----
export const campaignBuilderEnrich = (payload) =>
    api
        .post("/ads/campaign-builder/enrich", payload, { timeout: 90000 })
        .then((r) => r.data);
export const campaignBuilderPlan = (payload) =>
    api
        .post("/ads/campaign-builder/plan", payload, { timeout: 180000 })
        .then((r) => r.data);
export const campaignBuilderPush = (payload) =>
    api
        .post("/ads/campaign-builder/push", payload, { timeout: 180000 })
        .then((r) => r.data);
export const campaignBuilderPushNegatives = (payload) =>
    api
        .post("/ads/campaign-builder/push-negatives", payload, { timeout: 90000 })
        .then((r) => r.data);
export const campaignBuilderSuggestNegatives = (payload) =>
    api
        .post("/ads/campaign-builder/suggest-negatives", payload, { timeout: 90000 })
        .then((r) => r.data);

// ---- Market Research & Prospecting ----
export const marketResearchAnalyze = (payload) =>
    api
        .post("/market-research/analyze", payload, { timeout: 180000 })
        .then((r) => r.data);

// ---- Auction Insights AI ----
export const auctionInsightsAi = (payload) =>
    api
        .post("/ads/auction-insights/ai", payload, { timeout: 120000 })
        .then((r) => r.data);

export const auctionInsightsFetch = (payload) =>
    api
        .post("/ads/auction-insights/fetch", payload, { timeout: 90000 })
        .then((r) => r.data);
export const rivalAdCopyScrape = (payload) =>
    api
        .post("/ads/rival-adcopy/scrape", payload, { timeout: 90000 })
        .then((r) => r.data);

// ---- AI Ad-Copy Variations ----
export const generateAdVariations = (payload) =>
    api
        .post("/ads/ad-variations/generate", payload, { timeout: 60000 })
        .then((r) => r.data);

// ---- Ad Group → Responsive Search Ad generator (Claude-powered) ----
export const generateRSAsForAdGroups = (payload) =>
    api
        .post("/ads/ad-groups/generate-rsa", payload, { timeout: 180000 })
        .then((r) => r.data);

// ---- Three-Bucket Keyword Distiller ----
export const distillKeywords = (payload) =>
    api
        .post("/keywords/distill", payload, { timeout: 180000 })
        .then((r) => r.data);

// ---- SERP related-searches lookup (for KP "Import related searches") ----
export const historyRelatedLookup = (params) =>
    api
        .get("/history/related-lookup", { params, timeout: 30000 })
        .then((r) => r.data);

// ---- AI Search-term scoring (STA Manual Review) ----
export const adsSearchTermsScore = (payload) =>
    api
        .post("/ads/search-terms/score", payload, { timeout: 180000 })
        .then((r) => r.data);

// ---- Search-term Optimiser sync (STA action bundle → Editor CSVs) ----
export const adsSearchTermsSync = (payload) =>
    api
        .post("/ads/search-terms/sync", payload, { timeout: 60000 })
        .then((r) => r.data);

export const adsSearchTermsProcessed = (customer_id) =>
    api
        .get("/ads/search-terms/processed", { params: { customer_id }, timeout: 30000 })
        .then((r) => r.data);

// ---- Ad Watch (Daily Ad Monitor) ----
export const listWatches = () => api.get("/watches").then((r) => r.data);
export const createWatch = (payload) => api.post("/watches", payload).then((r) => r.data);
export const deleteWatch = (id) => api.delete(`/watches/${id}`).then((r) => r.data);
export const simulateWatch = (id) =>
    api.post(`/watches/${id}/simulate`, {}, { timeout: 240000 }).then((r) => r.data);
export const listWatchReports = (id) =>
    api.get(`/watches/${id}/reports`).then((r) => r.data);
export const reportPdfUrl = (reportId) => `${API}/reports/${reportId}/pdf`;
export const reportCsvUrl = (reportId) => `${API}/reports/${reportId}/csv`;
